import { useState, useEffect } from 'react';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Phone, Mail, MessageCircle, Send } from 'lucide-react';
import { toast } from 'sonner';

export default function ContactPage() {
  const [content, setContent] = useState(null);
  const [form, setForm] = useState({ name: '', email: '', message: '' });
  const [sending, setSending] = useState(false);

  useEffect(() => {
    api.get('/settings/contact').then(r => setContent(r.data)).catch(() => {});
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) { toast.error('Please fill all fields'); return; }
    setSending(true);
    try {
      await api.post('/contact', form);
      toast.success('Message sent! We will get back to you soon.');
      setForm({ name: '', email: '', message: '' });
    } catch { toast.error('Failed to send message'); }
    finally { setSending(false); }
  };

  return (
    <div data-testid="contact-page" className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
      <div className="text-center mb-12">
        <h1 className="text-4xl sm:text-5xl font-serif font-medium tracking-tight mb-3">{content?.heading || 'Get in Touch'}</h1>
        <p className="text-muted-foreground max-w-lg mx-auto">{content?.description || 'We would love to hear from you.'}</p>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
        <div>
          <div className="space-y-6 mb-8">
            {content?.whatsapp && (
              <a href={`https://wa.me/91${(content.whatsapp || '').replace(/[^0-9]/g, '')}`} target="_blank" rel="noreferrer" data-testid="whatsapp-link"
                className="flex items-center gap-4 p-5 bg-card rounded-2xl border border-border hover:border-primary/50 transition-colors">
                <div className="w-12 h-12 rounded-full bg-green-50 flex items-center justify-center"><MessageCircle className="h-6 w-6 text-green-600" /></div>
                <div><p className="font-medium text-sm">WhatsApp</p><p className="text-xs text-muted-foreground">{content.whatsapp}</p></div>
              </a>
            )}
            {content?.phone && (
              <a href={`tel:${content.phone}`} data-testid="phone-link"
                className="flex items-center gap-4 p-5 bg-card rounded-2xl border border-border hover:border-primary/50 transition-colors">
                <div className="w-12 h-12 rounded-full bg-blue-50 flex items-center justify-center"><Phone className="h-6 w-6 text-blue-600" /></div>
                <div><p className="font-medium text-sm">Phone</p><p className="text-xs text-muted-foreground">{content.phone}</p></div>
              </a>
            )}
            {content?.email && (
              <a href={`mailto:${content.email}`} data-testid="email-link"
                className="flex items-center gap-4 p-5 bg-card rounded-2xl border border-border hover:border-primary/50 transition-colors">
                <div className="w-12 h-12 rounded-full bg-amber-50 flex items-center justify-center"><Mail className="h-6 w-6 text-amber-600" /></div>
                <div><p className="font-medium text-sm">Email</p><p className="text-xs text-muted-foreground">{content.email}</p></div>
              </a>
            )}
          </div>
        </div>
        <div className="bg-card rounded-3xl border border-border p-8 shadow-sm">
          <h3 className="font-serif font-medium text-lg mb-5">Send us a message</h3>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div><Label>Name</Label><Input data-testid="contact-name" value={form.name} onChange={e => setForm({...form, name: e.target.value})} className="mt-1 rounded-2xl" /></div>
            <div><Label>Email</Label><Input data-testid="contact-email" type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} className="mt-1 rounded-2xl" /></div>
            <div><Label>Message</Label><Textarea data-testid="contact-message" value={form.message} onChange={e => setForm({...form, message: e.target.value})} className="mt-1 rounded-2xl" rows={5} /></div>
            <Button data-testid="contact-submit" type="submit" className="w-full rounded-full" disabled={sending}>
              <Send className="h-4 w-4 mr-2" /> {sending ? 'Sending...' : 'Send Message'}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
